import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchSessions, fetchProfiles, createSession, endSession } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { AgentSession } from '@/lib/agent-types';

type SessionSidebarProps = {
  activeSessionId: string | null;
  onSelectSession: (id: string) => void;
  onDeleteSession?: (id: string) => void;
};

function formatRelativeTime(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    completed: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    error: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  };

  return (
    <span className={cn('rounded px-1.5 py-0.5 text-xs', colors[status] ?? colors.active)}>
      {status}
    </span>
  );
}

function SessionItem({
  session,
  isActive,
  onClick,
  onDelete,
  isDeleting,
}: {
  session: AgentSession;
  isActive: boolean;
  onClick: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  return (
    <div
      className={cn(
        'group relative rounded-md transition-colors',
        isActive ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
      )}
    >
      <button type="button" onClick={onClick} className="w-full px-3 py-2 text-left text-sm">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate font-medium">{session.profileId}</span>
          <StatusBadge status={session.status} />
        </div>
        <div className="text-muted-foreground mt-0.5 text-xs">
          {formatRelativeTime(session.createdAt)}
        </div>
      </button>
      {session.status !== 'completed' && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          disabled={isDeleting}
          className={cn(
            'absolute top-1.5 right-1.5 rounded p-0.5 text-xs opacity-0 transition-opacity',
            'text-muted-foreground hover:text-destructive hover:bg-destructive/10',
            'group-hover:opacity-100',
            isDeleting && 'opacity-100 cursor-not-allowed'
          )}
          title="End session"
        >
          {isDeleting ? '...' : '\u00d7'}
        </button>
      )}
    </div>
  );
}

function NewSessionControls({ onSelectSession }: { onSelectSession: (id: string) => void }) {
  const queryClient = useQueryClient();
  const [selectedProfileId, setSelectedProfileId] = useState('');

  const { data: profilesData, isLoading: profilesLoading } = useQuery({
    queryKey: ['profiles'],
    queryFn: fetchProfiles,
  });

  const profiles = profilesData?.profiles ?? [];

  const mutation = useMutation({
    mutationFn: (profileId: string) => createSession(profileId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      onSelectSession(data.session.id);
    },
  });

  const handleCreate = () => {
    if (!selectedProfileId) return;
    mutation.mutate(selectedProfileId);
  };

  if (profilesLoading) {
    return <div className="text-muted-foreground px-3 text-xs">Loading profiles...</div>;
  }

  if (profiles.length === 0) {
    return <div className="text-muted-foreground px-3 text-xs">No profiles available</div>;
  }

  return (
    <div className="flex gap-2 px-3">
      <select
        value={selectedProfileId}
        onChange={(e) => setSelectedProfileId(e.target.value)}
        className="border-input bg-background min-w-0 flex-1 rounded-md border px-2 py-1 text-sm"
      >
        <option value="">Select profile</option>
        {profiles.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
      <Button size="sm" onClick={handleCreate} disabled={!selectedProfileId || mutation.isPending}>
        New
      </Button>
    </div>
  );
}

export function SessionSidebar({
  activeSessionId,
  onSelectSession,
  onDeleteSession,
}: SessionSidebarProps) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => fetchSessions(),
  });

  const deleteMutation = useMutation({
    mutationFn: (sessionId: string) => endSession(sessionId),
    onSuccess: (_data, sessionId) => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      onDeleteSession?.(sessionId);
    },
  });

  const sessions = data?.sessions ?? [];

  return (
    <div className="flex h-full flex-col gap-3 py-3">
      <div className="px-3 text-sm font-semibold">Sessions</div>
      <NewSessionControls onSelectSession={onSelectSession} />
      <div className="flex-1 overflow-y-auto px-1">
        {isLoading && <div className="text-muted-foreground px-3 text-xs">Loading sessions...</div>}
        {!isLoading && sessions.length === 0 && (
          <div className="text-muted-foreground px-3 text-xs">No sessions yet</div>
        )}
        <div className="flex flex-col gap-1">
          {sessions.map((session) => (
            <SessionItem
              key={session.id}
              session={session}
              isActive={session.id === activeSessionId}
              onClick={() => onSelectSession(session.id)}
              onDelete={() => deleteMutation.mutate(session.id)}
              isDeleting={deleteMutation.isPending && deleteMutation.variables === session.id}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
